/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { Col, Form, FormInstance, Row, Upload, UploadFile, UploadProps } from 'antd';
import { useState } from 'react';

import './NodeConfigUploadForm.css';

import { NodeConfig } from '@/client';
import { ValidationError } from '@/components/Forms/ValidationError';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Input } from '@/primitives/Input';
import { Select } from '@/primitives/Select';
import { tokens } from '@/theme';

import { ParentConfigSelector } from './ParentConfigSelector';

export type NodeConfigUploadFormProps = {
  loading: boolean;
  form: FormInstance<NodeConfigUploadFormFields>;
  mode: 'new' | 'revision';
  preSelectedConfig?: NodeConfig;
  hasApiError?: boolean;
  onClearApiErrors?: () => void;
};

export type NodeConfigUploadFormFields = {
  configFile?: UploadFile;
  configSchema?: string;
  name?: string;
  os?: 'windows' | 'linux';
  parentConfigId?: string;
};

const requiredMarkAfter = (label: React.ReactNode) => label;
const NAME_MAX_LENGTH = 100;
const CONFIG_SCHEMA_MAX_LENGTH = 15;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export function NodeConfigUploadForm({
  loading,
  form,
  mode,
  preSelectedConfig,
  hasApiError,
  onClearApiErrors,
}: NodeConfigUploadFormProps) {
  const { t } = useMimicTranslation('nodeConfigurations');
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [hasFileError, setHasFileError] = useState(false);

  const isRevisionMode = mode === 'revision';

  // Simple field disable logic - only for revision mode
  const isFieldDisabled = (fieldName: string) => {
    if (loading) return true;
    if (isRevisionMode) {
      return !['configFile', 'configSchema'].includes(fieldName);
    }
    return false;
  };

  // Generate default config schema version
  const getDefaultConfigSchema = () => {
    if (isRevisionMode && preSelectedConfig) {
      const nextVersion = (preSelectedConfig.latestRevisionNumber || 1) + 1;
      return `v1.0.${nextVersion}`;
    }
    return 'v1.0.1';
  };

  const validateFile = (file: File): string | null => {
    const isJson = file.type === 'application/json' || file.name.endsWith('.json');
    if (!isJson) {
      return t('upload.form.errors.fileRequiredJson');
    }

    if (file.size >= MAX_FILE_SIZE) {
      return t('upload.form.errors.fileTooLarge');
    }

    return null;
  };

  const beforeUpload: UploadProps['beforeUpload'] = (file) => {
    const error = validateFile(file);
    if (error) {
      setHasFileError(true);
      form.setFields([{ name: 'configFile', errors: [error] }]);
      return false;
    }
    setHasFileError(false);
    return false; // Prevent auto upload
  };

  const handleFileChange: UploadProps['onChange'] = ({ fileList: newFileList }) => {
    // Clear errors when file changes (removed or replaced)
    setHasFileError(false);
    onClearApiErrors?.();

    if (newFileList.length > 0) {
      const file = newFileList[0];
      if (file.originFileObj) {
        // Validate JSON content asynchronously
        const reader = new FileReader();

        reader.onload = (e) => {
          try {
            const content = e.target?.result as string;
            JSON.parse(content);
            // Valid JSON - keep the file
            setFileList([file]);
            setHasFileError(false);
            form.setFieldValue('configFile', file);
            form.setFields([{ name: 'configFile', errors: [] }]);
          } catch {
            // Invalid JSON - reject the file but show error
            setFileList([]);
            setHasFileError(true);
            form.setFieldValue('configFile', undefined);

            form.setFields([
              {
                name: 'configFile',
                errors: [
                  <ValidationError key="invalid-json">{t('upload.form.errors.invalidJsonFile')}</ValidationError>,
                ] as unknown as string[],
              },
            ]);
          }
        };

        reader.readAsText(file.originFileObj);
      }
    } else {
      setFileList([]);
      form.setFieldValue('configFile', undefined);
      form.setFields([{ name: 'configFile', errors: [] }]);
    }
  };

  const handleParentConfigSelect = (config: NodeConfig | null) => {
    if (config) {
      const nextVersion = (config.latestRevisionNumber || 1) + 1;

      form.setFieldsValue({
        parentConfigId: config.id,
        name: config.name,
        os: config.os === 'not_applicable' ? undefined : (config.os as 'windows' | 'linux'),
        configSchema: `v1.0.${nextVersion}`,
      });
    } else {
      form.setFieldsValue({
        parentConfigId: undefined,
        name: undefined,
        os: undefined,
        configSchema: 'v1.0.1',
      });
    }
  };

  const initialValues = {
    name: preSelectedConfig?.name,
    os: preSelectedConfig?.os === 'not_applicable' ? undefined : (preSelectedConfig?.os as 'windows' | 'linux'),
    configSchema: getDefaultConfigSchema(),
  };

  return (
    <Form
      form={form}
      layout="vertical"
      requiredMark={requiredMarkAfter}
      validateTrigger="onBlur"
      initialValues={initialValues}
      style={{ padding: '0.5em' }}
    >
      {/* Parent Configuration Selector - only for new configs */}
      {!isRevisionMode && (
        <ParentConfigSelector disabled={isFieldDisabled('parentConfigId')} onConfigSelect={handleParentConfigSelect} />
      )}

      {/* Configuration File Upload */}
      <Form.Item<NodeConfigUploadFormFields>
        label={t('upload.form.configurationFile')}
        name="configFile"
        className="config-file-upload"
        rules={[
          {
            required: true,
            message: <ValidationError>{t('upload.form.errors.configurationFileRequired')}</ValidationError>,
          },
        ]}
      >
        <Upload.Dragger
          fileList={fileList}
          beforeUpload={beforeUpload}
          onChange={handleFileChange}
          accept=".json,application/json"
          maxCount={1}
          disabled={isFieldDisabled('configFile')}
          style={{
            backgroundColor: isFieldDisabled('configFile') ? '#f5f5f5' : 'inherit',
            borderColor: hasFileError || hasApiError ? tokens.color.error.error : undefined,
          }}
        >
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <p style={{ fontSize: '16px', marginBottom: '8px' }}>{t('upload.form.configurationFileUploadText')}</p>
            <p style={{ color: '#8c8c8c', fontSize: '14px', margin: 0 }}>
              {t('upload.form.configurationFileUploadSubtext')}
            </p>
          </div>
        </Upload.Dragger>
      </Form.Item>

      {/* Configuration Schema Version */}
      <Form.Item<NodeConfigUploadFormFields>
        label={t('upload.form.configurationSchemaVersion')}
        name="configSchema"
        help={t('upload.form.configurationSchemaVersionHelp')}
        rules={[
          {
            required: true,
            message: <ValidationError>{t('upload.form.errors.configurationSchemaVersionRequired')}</ValidationError>,
          },
          {
            max: CONFIG_SCHEMA_MAX_LENGTH,
            message: (
              <ValidationError>
                {t('upload.form.errors.configurationSchemaVersionTooLong', { maxLength: CONFIG_SCHEMA_MAX_LENGTH })}
              </ValidationError>
            ),
          },
        ]}
      >
        <Input
          placeholder={t('upload.form.configurationSchemaVersionPlaceholder')}
          disabled={isFieldDisabled('configSchema')}
          count={{ max: CONFIG_SCHEMA_MAX_LENGTH }}
          style={{ backgroundColor: '#ffffff' }}
        />
      </Form.Item>

      {/* Configuration Name and Operating System - only for new configs */}
      {!isRevisionMode && (
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item<NodeConfigUploadFormFields>
              label={t('upload.form.configurationName')}
              name="name"
              rules={[
                {
                  required: true,
                  message: <ValidationError>{t('upload.form.errors.configurationNameRequired')}</ValidationError>,
                },
                {
                  max: NAME_MAX_LENGTH,
                  message: (
                    <ValidationError>
                      {t('upload.form.errors.configurationNameTooLong', { maxLength: NAME_MAX_LENGTH })}
                    </ValidationError>
                  ),
                },
              ]}
            >
              <Input
                placeholder={t('upload.form.configurationNamePlaceholder')}
                disabled={isFieldDisabled('name')}
                count={{ max: NAME_MAX_LENGTH }}
                style={{ backgroundColor: '#ffffff' }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item<NodeConfigUploadFormFields>
              label={t('upload.form.operatingSystem')}
              name="os"
              rules={[
                {
                  required: true,
                  message: <ValidationError>{t('upload.form.errors.operatingSystemRequired')}</ValidationError>,
                },
              ]}
            >
              <Select
                placeholder={t('upload.form.operatingSystemPlaceholder')}
                disabled={isFieldDisabled('os')}
                options={[
                  { label: t('upload.form.windows'), value: 'windows' },
                  { label: t('upload.form.linux'), value: 'linux' },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>
      )}
    </Form>
  );
}
